import {
  type BaseSyntheticEvent,
  type FocusEventHandler,
  useEffect,
  useMemo,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { isUndefined } from "lodash";
import {
  type Control,
  type SubmitErrorHandler,
  useForm,
} from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { invalidateOrderRelatedQueries } from "~/v2/api/invalidateOrderRelatedQueries";
import type { Account, Margin, Position } from "~/v2/api/rest/account";
import type { Clock, OptionContract, Quote } from "~/v2/api/rest/data";
import { createOrder } from "~/v2/api/rest/order";
import {
  DEFAULT_CONTRACT_MULTIPLIER,
  OPTION_ORDER_TYPE_OPTIONS,
  type OptionOrderType,
  type OptionTimeInForce,
  POSITION_INTENT_OPTIONS,
  type PositionIntent,
} from "~/v2/constants/optionOrder";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import {
  amplitudeTradeTracking,
  type OrderEvent,
} from "~/v2/pages/dashboard/tradeV2/amplitudeTradeTracking";
import {
  type BuyingPowerResult,
  useBuyingPower,
} from "../shared/option/hooks/useBuyingPower";
import {
  type TimeInForceOption,
  useTimeInForceOptions,
} from "../shared/option/hooks/useTimeInForceOptions";
import { concatFormErrorMessages } from "../shared/utils";
import { useContractDisplay } from "./hooks/useContractDisplay";
import { useEstimatedCost } from "./hooks/useEstimatedCost";
import { type FeesResult, useFees } from "./hooks/useFees";
import {
  type QuoteDisplayResult,
  useQuoteDisplay,
} from "./hooks/useQuoteDisplay";
import { type UnderlyingDisplay, useUnderlying } from "./hooks/useUnderlying";
import {
  type OptionSchema,
  optionSchemaSuperRefinement,
  zOptionSchema,
} from "./schema";
import {
  createOptionOrderWithIntentRequest,
  getDefaultPositionIntent,
  getIsSubmitDisabled,
  getSideFromIntent,
} from "./utils";

const SUBMIT_COMPLETED_TIMEOUT = 500;

const DEFAULT_FORM_VALUES: Partial<OptionSchema> = {
  orderType: "market",
  timeInForce: "day",
  quantity: 1,
};

interface UseOptionOrderTicketControllerProps {
  account: Account | undefined;
  product: string | undefined;
  contract: OptionContract | undefined;
  quote: Quote | undefined;
  margin: Margin | undefined;
  clock: Clock | undefined;
  symbol: string;
  price?: number;
  quoteSide?: string;
  onClearSelection?: () => void;
  positions?: Position[];
  initialQuantity?: number;
}

export const useOptionOrderTicketController = ({
  account,
  product,
  contract,
  quote,
  margin,
  clock,
  symbol,
  price,
  quoteSide,
  onClearSelection,
  positions,
  initialQuantity,
}: UseOptionOrderTicketControllerProps): UseOptionControllerReturn => {
  const stopOrdersEnabled = useFeatureEnabled(FeatureFlags.OPTION_STOP_ORDERS);

  const [isSubmitCompleted, setIsSubmitCompleted] = useState(false);

  const [positionIntent, setPositionIntent] = useState<PositionIntent>(
    getDefaultPositionIntent(quoteSide, symbol, positions),
  );

  const positionQty = positions?.find((p) => p.symbol === symbol)?.qty;

  // biome-ignore lint/correctness/useExhaustiveDependencies: positionQty is a deliberate primitive stand-in for positions: it changes when the held quantity changes, whereas the positions array gets a new identity on every fetch, so depending on the array directly would recompute the position intent (and stomp the user's selection) far more often than intended.
  useEffect(() => {
    setPositionIntent(getDefaultPositionIntent(quoteSide, symbol, positions));
  }, [quoteSide, symbol, positionQty]);

  const side = getSideFromIntent(positionIntent);
  const isCredit = side === "sell";

  const { underlying } = useUnderlying({
    underlyingSymbol: contract?.underlying_symbol,
  });

  const {
    setValue,
    watch,
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<OptionSchema>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      quantity:
        initialQuantity && initialQuantity > 0
          ? Math.max(1, Math.round(initialQuantity))
          : 1,
      orderType: !isUndefined(price) ? "limit" : "market",
      limitPrice: price,
    },
    mode: "onChange",
    resolver: zodResolver(
      zOptionSchema.superRefine(optionSchemaSuperRefinement),
    ),
  });

  const orderType = watch("orderType");
  const limitPrice = watch("limitPrice");
  const stopPrice = watch("stopPrice");
  const quantity = watch("quantity");
  const timeInForce = watch("timeInForce");

  const orderEventData: OrderEvent = {
    order_type: orderType,
    asset_symbol: symbol,
    asset_class: "us_option",
    qty: quantity,
    time_in_force: timeInForce,
  };

  const { bid, ask, midPrice, contractPrice, quoteLastUpdated, marketPrice } =
    useQuoteDisplay({ quote, side });

  const { strikeAndType, expirationDate } = useContractDisplay(contract);

  const buyingPower = useBuyingPower({ margin });

  const estimatedCost = useEstimatedCost({
    hasQuote: !!quote,
    quantity,
    orderType,
    limitPrice,
    stopPrice,
    marketPrice,
    multiplier: contract?.multiplier || DEFAULT_CONTRACT_MULTIPLIER,
  });

  const fees = useFees({ quantity, isSell: side === "sell" });

  const timeInForceOptions = useTimeInForceOptions(orderType, clock);

  const onOrderTypeChange = (newOrderType: OptionOrderType) => {
    setValue("orderType", newOrderType);
    setValue("limitPrice", undefined);
    setValue("stopPrice", undefined);
    setValue("timeInForce", "day");
    amplitudeTradeTracking.orderTypeChanged(newOrderType);
  };

  const onTimeInForceChange = (newTimeInForce: OptionTimeInForce) => {
    setValue("timeInForce", newTimeInForce);
  };

  const onPositionIntentChange = (intent: PositionIntent) => {
    setPositionIntent(intent);
    amplitudeTradeTracking.positionIntentChanged(intent, orderEventData);
  };

  const isSubmitDisabled = useMemo(
    () =>
      isSubmitCompleted ||
      getIsSubmitDisabled({
        account,
        contract,
        quote,
        product,
        quantity,
        orderType,
        limitPrice,
        stopPrice,
      }),
    [
      account,
      contract,
      quote,
      product,
      quantity,
      orderType,
      limitPrice,
      stopPrice,
      isSubmitCompleted,
    ],
  );

  const queryClient = useQueryClient();
  const invalidateOrderAndPositionQueries = () => {
    invalidateOrderRelatedQueries(queryClient);
  };

  const mutation = useMutationRequest(
    (data: OptionSchema & { positionIntent: PositionIntent }) =>
      createOrder(
        account?.id ?? "",
        createOptionOrderWithIntentRequest({ ...data, symbol }),
        product as "paper" | "live",
      ),
    {
      onSuccess: () => {
        toastSuccess("Your order has been placed successfully");
        invalidateOrderAndPositionQueries();
        setIsSubmitCompleted(true);
        setTimeout(() => {
          setIsSubmitCompleted(false);
          resetForm(DEFAULT_FORM_VALUES);
        }, SUBMIT_COMPLETED_TIMEOUT);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Order failed: ${errorMessage}`);
      },
    },
  );

  const onValidSubmit = (data: OptionSchema) => {
    if (!mutation.isPending && (product === "live" || product === "paper")) {
      mutation.mutate({ ...data, positionIntent });
      amplitudeTradeTracking.confirmOrderClicked(orderEventData);
    }
  };

  const onInvalidSubmit: SubmitErrorHandler<OptionSchema> = (errors) =>
    toastError(`Invalid Order: ${concatFormErrorMessages(errors)}`);

  const onSubmit = handleSubmit(onValidSubmit, onInvalidSubmit);

  const onCancel = () => {
    resetForm({
      ...DEFAULT_FORM_VALUES,
      orderType: !isUndefined(price) ? "limit" : "market",
      limitPrice: price,
    });
    onClearSelection?.();
  };

  const onQuantityBlur: FocusEventHandler<HTMLInputElement> = (event) =>
    amplitudeTradeTracking.quantityChanged(event.target.value);

  // biome-ignore lint/correctness/useExhaustiveDependencies: symbol is the trigger: the form is reset to defaults when the user switches contract. The body writes rather than reads it.
  useEffect(() => {
    resetForm({
      ...DEFAULT_FORM_VALUES,
      orderType: !isUndefined(price) ? "limit" : "market",
      limitPrice: price,
    });
  }, [symbol, price, resetForm]);

  return {
    control,
    onSubmit,
    onCancel,
    onQuantityBlur,
    isSubmitDisabled,
    isSubmitLoading: mutation.isPending,
    isSubmitCompleted,

    positionIntent,
    onPositionIntentChange,
    positionIntentOptions: POSITION_INTENT_OPTIONS,
    isCredit,

    underlying,

    hasQuote: !!quote,
    hasContract: !!contract,

    bid,
    ask,
    midPrice,
    contractPrice,
    quoteLastUpdated,

    estimatedCost,
    buyingPower,
    fees,

    orderType,
    onOrderTypeChange,
    orderTypeOptions: stopOrdersEnabled
      ? OPTION_ORDER_TYPE_OPTIONS
      : OPTION_ORDER_TYPE_OPTIONS.filter(
          (o) => o.value !== "stop" && o.value !== "stop_limit",
        ),

    timeInForce,
    onTimeInForceChange,
    timeInForceOptions,

    strikeAndType,
    expirationDate,
    contractMultiplier: contract?.multiplier || DEFAULT_CONTRACT_MULTIPLIER,
  };
};

export type { UnderlyingDisplay } from "./hooks/useUnderlying";

export type UseOptionControllerReturn = {
  control: Control<OptionSchema>;
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  onCancel: () => void;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  isSubmitDisabled: boolean;
  isSubmitLoading: boolean;
  isSubmitCompleted: boolean;

  positionIntent: PositionIntent;
  onPositionIntentChange: (intent: PositionIntent) => void;
  positionIntentOptions: typeof POSITION_INTENT_OPTIONS;
  isCredit: boolean;

  underlying: UnderlyingDisplay;
  hasQuote: boolean;
  hasContract: boolean;

  bid: string;
  ask: string;
  midPrice: QuoteDisplayResult["midPrice"];
  contractPrice: string;
  quoteLastUpdated: string | undefined;

  estimatedCost: string;
  buyingPower: BuyingPowerResult;
  fees: FeesResult;

  orderType: OptionOrderType;
  onOrderTypeChange: (orderType: OptionOrderType) => void;
  orderTypeOptions: { value: OptionOrderType; label: string }[];

  timeInForce: OptionTimeInForce;
  onTimeInForceChange: (timeInForce: OptionTimeInForce) => void;
  timeInForceOptions: TimeInForceOption[];

  strikeAndType: string;
  expirationDate: string;
  contractMultiplier: string;
};
